import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Grid, Box, IconButton, Typography } from "@mui/material/";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import actions from "../../../../Store/actions";
import { enums, helpers } from "../../../../common/lib";
import { strings } from "../../../../common/i18n";
import { logToSentry } from "../../../../config/Sentry";
import Table from "./Table";
import styles from "./styles";
import {
    ListFilters,
    AuditDetails,
    Auditors,
    AuditComment
} from "../LeftComponents/";
import { AuditSummaryDownloadButton } from "../../../../common/components";
import DonutChart from "../../../../common/components/DonutChart";
import AssetProgresBar from "../../../../common/components/AssetProgresBar";
import CloseAudit from "./CloseAuditPopup";
import AbandonAuditButton from "./AbandonAuditButton";
import View from "./View";
import Signees from "../LeftComponents/Signees/";
import PieChartRoundedIcon from "@mui/icons-material/PieChartRounded";
import TableRowsIcon from "@mui/icons-material/TableRows";
import {
    usePendingCountProgress,
    useShouldShowCloseAuditPopup,
    useSignatureRequired
} from "../../../../common/hooks";
import auditStatusDetail from "../../../Status/auditStatusDetail";

/**
 * Audit Summary Component
 */

export default () => {
    const typesList = useSelector((state) => state.typesList);
    const audits = useSelector((state) => state.audits);
    const dealerContacts = useSelector((state) => state.dealerContacts);
    const auditReview = useSelector((state) => state.auditReview);
    const auditsList = useSelector((state) => state.auditsList);
    let navigate = useNavigate();
    const { pathname } = useLocation();
    const isSignatureRequired = useSignatureRequired();
    const canCloseAudit = useShouldShowCloseAuditPopup(isSignatureRequired);
    const pendingCountProgress = usePendingCountProgress();
    const isClosedAudit = auditsList.item?.status === enums.status.audit.closed;
    const canAbandonAudit =
        !!auditStatusDetail[auditsList.item?.status]?.abandonAudit;
    const [view, setView] = useState(isClosedAudit ? "chart" : "table");

    const [closeDialog, setCloseDialog] = useState(canCloseAudit);

    useEffect(() => {
        if (isSignatureRequired && dealerContacts.length < 2)
            setCloseDialog(canCloseAudit);
        else setCloseDialog(canCloseAudit);
    }, [isSignatureRequired, canCloseAudit]);

    const dispatch = useDispatch();
    /** Get audit status  */
    const getAuditStatus = (statusId) => {
        const { auditStatus } = typesList;
        return helpers.getListItemForId(auditStatus, statusId);
    };

    const summaryData = {
        status: getAuditStatus(audits?.item?.status),
        assetSummary: {
            altAsset: audits?.item?.altAssetsSummary,
            auditAsset: {
                unchecked: audits?.item?.auditAssetsSummary.outstanding,
                ...audits?.item?.auditAssetsSummary
            }
        }
    };

    const onBack = () => {
        try {
            const auditPath = pathname.split("/").slice(0, -1).join("/");
            navigate(`${auditPath}/inprogress`);
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On navigating to inprogess:", { error });
            dispatch(
                actions.toast.create.error(strings("errors.onBackAudits"))
            );
        }
    };
    const handleToggleView = (selectedView) => setView(selectedView);

    return (
        <>
            <Box sx={styles.mainHeaderContainer}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <IconButton onClick={onBack}>
                        <ArrowBackIosNewIcon color="primary" />
                    </IconButton>
                    <Typography variant="h6" sx={styles.mainHeader}>
                        {`${strings("review")} - ${audits?.item?.name}`}
                    </Typography>
                </Box>
                {canAbandonAudit && <AbandonAuditButton />}
            </Box>
            {pendingCountProgress && (
                <Box sx={styles.pendingCountBanner}>
                    <WarningAmberOutlinedIcon fontSize="small" />
                    <Typography variant="body2" color="inherit">
                        {strings("messages.pendingCountReviewWarning", {
                            counted: pendingCountProgress.counted,
                            total: pendingCountProgress.total
                        })}
                    </Typography>
                </Box>
            )}
            <Grid container sx={styles.assetListContainer}>
                <Grid item sx={styles.leftContainer}>
                    <Grid
                        container
                        direction="column"
                        justifyContent="space-evenly"
                        alignItems="stretch"
                        sx={styles.leftContainerColumn}
                    >
                        <Grid sx={styles.assetProgressContainer}>
                            <AssetProgresBar data={summaryData} />
                            <Typography
                                variant="caption"
                                sx={styles.auditStatus}
                            >
                                {getAuditStatus(audits?.item?.status).name}
                            </Typography>
                        </Grid>
                        <Grid>
                            <ListFilters onToggleView={handleToggleView} />
                        </Grid>
                        <Grid>
                            <AuditDetails />
                        </Grid>
                        <Grid>
                            <Auditors />
                        </Grid>
                        <Grid>
                            <AuditComment />
                        </Grid>
                        {isSignatureRequired && (
                            <Grid>
                                <Signees dealers={dealerContacts} />
                            </Grid>
                        )}
                        <AuditSummaryDownloadButton />
                    </Grid>
                </Grid>
                <Grid item sx={styles.rightContainer}
                >
                    <Grid justifySelf="right">
                        <TableRowsIcon
                            color="primary"
                            onClick={() => handleToggleView("table")}
                            style={styles.getIconStyle(view, "table")}
                        />
                        <PieChartRoundedIcon
                            color="primary"
                            onClick={() => handleToggleView("chart")}
                            style={styles.getIconStyle(view, "chart")}
                        />
                    </Grid>
                    <Grid>
                        {view === "chart" ? (
                            <View onToggleView={handleToggleView} />
                        ) : (
                            <Table />
                        )}
                    </Grid>
                </Grid>
            </Grid>
            {canCloseAudit && (
                <CloseAudit
                    closeDialog={closeDialog}
                    setCloseDialog={setCloseDialog}
                />
            )}
        </>
    );
};

/** TODO
 *
 * After the view is finalised do the clean up
 */
